import React from "react";

/* 
 Scale Bar is a line that shows the distance in meters (or kilometers) on the map.
 Draw a line like |____| and show the distance in meters (or kilometers) on the line.
 The bar should be of between 50 and 200 pixels long.
 The number of meters (or kilometers) should be shown above the middle of the bar.
 The bar should be placed in the bottom left corner of the map.
 The bar length should correspond to the map zoom level.  
*/

type ScaleBarProps = {
  zoom: number;
};

const roundTo125PowerOfTen = (value: number) => {
  const log10 = Math.log10(value);
  const log10Floor = Math.floor(log10);
  const powerOfTen = Math.pow(10, log10Floor);
  const log10Remainder = log10 - log10Floor;
  const remainder = Math.pow(10, log10Remainder);
  let meters;
  if (remainder < 2) meters = powerOfTen;
  else if (remainder < 3) meters = 2 * powerOfTen;
  else if (remainder < 5) meters = 3 * powerOfTen;
  else if (remainder < 10) meters = 5 * powerOfTen;
  else meters = 10 * powerOfTen;
  return meters;
};

export const ScaleBar: React.FC<ScaleBarProps> = ({ zoom }) => {
  const calculateScale = (zoom: number) => {
    // Calculate the scale, number of pixels and distance meters based on the zoom level

    const scale = Math.pow(2, zoom);
    const initialMaximumSizeOfScaleBarInPixels = 256;
    const mapGlScalingConstant = 51360; // How many meters in the map view correspond to 2^8 pixels in zoom level 8.
    // Tuned manually via Google maps.
    // This works for Mercator projection in low and medium lattitudes, without tilting the map.
    const initialMeters = (initialMaximumSizeOfScaleBarInPixels * mapGlScalingConstant) / scale;
    const meters = roundTo125PowerOfTen(initialMeters);
    const pixels = (meters * scale) / mapGlScalingConstant;
    let distanceAsText;
    const thresholdForSwitchingToKilometers = 5000;
    if (meters < 5) distanceAsText = `${(meters * 100).toFixed()} cm`;
    else if (meters < thresholdForSwitchingToKilometers) distanceAsText = `${meters.toFixed()} m`;
    else distanceAsText = `${(meters / 1000).toFixed()} km`;
    return { distanceAsText, pixels };
  };

  const { distanceAsText, pixels } = calculateScale(zoom);

  return (
    <div
      style={{
        position: "absolute",
        bottom: 5,
        left: 100,
        backgroundColor: "rgba(255, 255, 255, 0.5)",
        padding: "2px",
      }}
    >
      <div style={{ borderBottom: "2px solid black", width: `${pixels}px` }}></div>
      <div style={{ textAlign: "center" }}>{distanceAsText}</div>
    </div>
  );
};
